/** E2E_ENVIRONMENT value for PR targeted E2E (targeted-e2e.yml). */
export const CI_TARGETED_E2E_ENVIRONMENT = 'ci-targeted'

/**
 * Split a tenants.domain value into individual domain entries.
 * @param {unknown} domainString
 * @returns {string[]}
 */
export function parseTenantDomainEntries(domainString) {
  if (typeof domainString !== 'string' || !domainString.trim()) {
    return []
  }
  return domainString
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean)
}

/**
 * Whether a hostname matches a single domain entry (exact or wildcard).
 * Wildcard entries use the `*.suffix` form, e.g. `*.example.com`.
 * @param {string} hostname
 * @param {string} domainEntry
 * @returns {boolean}
 */
export function hostnameMatchesTenantDomainEntry(hostname, domainEntry) {
  if (!hostname || !domainEntry) {
    return false
  }

  if (domainEntry === hostname) {
    return true
  }

  if (!domainEntry.startsWith('*.')) {
    return false
  }

  const suffix = domainEntry.slice(1)
  return hostname.endsWith(suffix) && hostname.length > suffix.length
}

/**
 * Whether a hostname matches any entry in a tenants.domain value.
 * @param {string} hostname
 * @param {unknown} domainString
 * @returns {boolean}
 */
export function hostnameMatchesTenantDomains(hostname, domainString) {
  return parseTenantDomainEntries(domainString).some((entry) =>
    hostnameMatchesTenantDomainEntry(hostname, entry),
  )
}

/**
 * Quote a value for PostgREST filters (eq, etc.).
 * @param {string} raw
 * @returns {string}
 */
function quotePostgrestValue(raw) {
  const escaped = String(raw).replace(/"/g, '""')
  return `"${escaped}"`
}

/**
 * Build a quoted ilike "contains" pattern for PostgREST `.or()` filters.
 * Uses `*` wildcards inside quotes — same convention as IlikePatternForSearch.
 * @param {string} raw
 * @returns {string}
 */
function buildIlikeContainsPattern(raw) {
  const escaped = String(raw)
    .replace(/[%_*]/g, (c) => `\\${c}`)
    .replace(/"/g, '""')
  return `"*${escaped}*"`
}

/**
 * Build a PostgREST `.or()` filter for tenant domain lookup candidates.
 * @param {string} hostname
 * @returns {string}
 */
export function buildTenantDomainQueryOrFilters(hostname) {
  const filters = new Set([
    `domain.eq.${quotePostgrestValue(hostname)}`,
    `domain.ilike.${buildIlikeContainsPattern(hostname)}`,
  ])

  return Array.from(filters).join(',')
}

/**
 * Pick the best tenant row for a hostname from query candidates.
 * @param {string} hostname
 * @param {Array<{ domain?: string }>|null|undefined} tenants
 * @returns {object|null}
 */
export function selectTenantByHostname(hostname, tenants) {
  if (!hostname || !Array.isArray(tenants) || tenants.length === 0) {
    return null
  }

  const matches = tenants.filter((tenant) => hostnameMatchesTenantDomains(hostname, tenant?.domain))

  if (matches.length === 0) {
    return null
  }

  if (matches.length === 1) {
    return matches[0]
  }

  const exactMatch = matches.find((tenant) =>
    parseTenantDomainEntries(tenant?.domain).some(
      (entry) => entry === hostname && !entry.startsWith('*.'),
    ),
  )

  return exactMatch ?? matches[0]
}

/**
 * Resolve the hostname used for tenant lookup.
 * ci-targeted E2E only: use tenantDomainOverride (VITE_E2E_CI_TENANT_DOMAIN).
 * @param {string} hostname
 * @param {{ e2eEnvironment?: string, tenantDomainOverride?: string }} [options]
 * @returns {string}
 */
export function resolveTenantLookupHostname(
  hostname,
  { e2eEnvironment = '', tenantDomainOverride = '' } = {},
) {
  const normalizedEnvironment = String(e2eEnvironment ?? '')
    .trim()
    .toLowerCase()

  if (normalizedEnvironment !== CI_TARGETED_E2E_ENVIRONMENT) {
    return hostname
  }

  const override = String(tenantDomainOverride ?? '').trim()
  if (!override) {
    throw new Error('VITE_E2E_CI_TENANT_DOMAIN is required when E2E_ENVIRONMENT is ci-targeted.')
  }

  return override
}
